<template>
  <div class="flex flex-col md:flex-row h-screen w-full pt-16 overflow-y-auto">
    <!-- TEXT PART -->
    <div class="flex flex-col flex-1 pt-4 items-center border-black px-4">
      <h1 class="font-black mb-2 text-xl text-teal-900">Text</h1>
      <!-- SENTENCE DISPLAY PART -->
      <div
        class="h-full flex flex-wrap justify-center items-center border-2 w-full mb-4 rounded border-teal-500 p-4 relative overflow-y-auto"
        @mouseup="getSelectionWithinInput">
        <div class="text-center text-base" v-if="currentSentence" v-html="currentSentence"></div>
        <hash-loader 
          :loading="this.loadingForData"
          color="#38A169"
          :size="75"
          sizeUnit="px"></hash-loader>
      </div>
      <!-- END OF SENTENCE DISPLAY PART -->
      <!-- NAVIGATION BUTTON -->
      <div class="mb-4">
        <button class="button-direction" @click="previousSentence">
          <i class="fas fa-arrow-left text-xl text-teal-500"></i></button>
          <p class="font-bold bg-white p-2 inline" v-if="sentences">{{ currentIndex + 1 }} / {{ sentences.length }}</p>
        <button class="button-direction" @click="nextSentence">
          <i class="fas fa-arrow-right text-xl text-teal-500"></i></button>
      </div>
      <!-- END OF NAVIGATION BUTTON -->
      <!-- ACTION BUTTON -->
      <div class="flex mb-2">
        <button @click="seePreviousList" class="button-action w-32 h-10 rounded mx-4 bg-teal-700 text-teal-200" :disabled="editing">
          See Previous List</button>
        <button @click="getSentences" class="button-action w-24 h-10 rounded mx-4 bg-teal-700 text-teal-200" :disabled="editing">
          Display</button>
        <button @click="getSentences" class="button-action w-24 h-10 rounded mx-4 bg-teal-700 text-teal-200" :disabled="editing">
          Predict</button>
        <button @click="saveResultAction" class="button-action w-24 h-10 rounded mx-4 bg-teal-700 text-teal-200" :disabled="!editing">
          Save</button>
        <button @click="trainResultAction" class="button-action w-24 h-10 rounded mx-4 bg-teal-700 text-teal-200">
          Train</button>
        </div>
        <!-- END OF ACTION BUTTON -->
    </div>
    <!-- END OF TEXT PART -->
    <!-- NER LIST PART -->
    <div class="flex flex-col md:w-2/6 pt-4 items-center border-black px-8">
      <h1 class="font-black mb-2 text-xl text-teal-900">NER List</h1>
      <div class="border-2 h-full w-full mb-2 rounded border-teal-500 height-more overflow-y-auto p-1">
        <!-- DISPLAY NER LIST -->
        <div v-if="sentences">
          <ner-list
            v-for="(item, index) in sentences[currentIndex].entities" :key="item.id"
            :index="index" 
            :data="item"
            :dropdownContent="dropdownContent" 
            :fullText="sentences[currentIndex].text"
            :deleteNERList="deleteNERList" 
            :startEditingNERListProcess="startEditingNERListProcess"
            :finishEditingNERListProcess="finishEditingNERListProcess"></ner-list>
        </div>
        <!-- END OF DISPLAY NER LIST -->
      </div>
      <!-- ADDING NER LIST BUTTON -->
      <button 
        :disabled="!editing" 
        class="button-plusmin mb-4 text-teal-200 w-full h-8 bg-teal-700 focus:outline-none flex items-center justify-center rounded"
        @click="addNERList">
        <i class="fas fa-plus text-xl"></i>
      </button>
    </div>
    <!-- END OF NER LIST PART -->
    <!-- PREVIOUS LIST COMPONENT -->
    <previous-list :data="previousListData" :show="showPreviousList" :goBack="seePreviousList"></previous-list>
  </div>
</template>

<script>
import axios from 'axios';
import _ from 'lodash';
import { HashLoader } from '@saeris/vue-spinners'
import { mapGetters, mapActions } from 'vuex';

import PreviousList from '@/components/PreviousList';
import NERList from '@/components/NERList';
import entityTypes from '@/entityTypes';
import { display } from '@/data';
import { baseUrl } from '@/config';
import { makeid } from '@/helpers';

export default {
  mounted() {
    this.fetchEntities();
  },
  data() {
    return {
      // main data
      sentences: '',
      // data for editting purposes
      currentIndex: 0,
      currentSentence: '',
      editing: false,
      loadingForData: false,
      // NER List data
      highlighted: 'No Highlighted word',
      onHighlighting: false,
      highlightedIndex: null,
      // Previous list
      showPreviousList: false,
      previousListData: [],
      // train and save result data
      savedResult: '',
      trainResult: ''
    }
  },
  components: {
    NerList: NERList,
    PreviousList,
    HashLoader
  },
  computed: {
    ...mapGetters({ dropdownContent: 'entities/getEntities' })
  },
  methods: {
    ...mapActions({ fetchEntities: 'entities/fetchEntities' }),
    /**
     * Escape some special character
     * @param {string} namedEntity - the named entity to be escaped
     */
    escapeRegExp(namedEntity) {
      return namedEntity.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
    },
    /**
     * process to higlight namedEntity and give color
     * and then set the currentSentence to displayed on the screen
     * @param {string} text - Text to be highlighted
     */
    hightlightIt(text) {
      if(!text) {
          return 'No Text';
      }

      const find = this.sentences[this.currentIndex].entities;
      let str = text;
      for (let i = 0; i < find.length; i++) {
        if (find[i].namedEntity) {
          str = str.replace(new RegExp(this.escapeRegExp(find[i].namedEntity) + "(?![^<]*</span>)"), match => {
            const color = this.dropdownContent.filter(content => content.name === find[i].entity_type)[0].color;
            return `<span style="background: ${color}">${match}</span>`;
          });
        }
      }
      this.currentSentence = str;
    },
    /**
     * getting text selected or higlighted by mouse
     * it will return the text string and coordinates
     */
    getSelectionWithinInput() {
      let selection = '';
      if (window.getSelection) {
          selection = window.getSelection().toString();
      } else if (document.selection && document.selection.type != "Control") {
          selection = document.selection.createRange().text;
      }

      if (selection && this.highlightedIndex !== null) {
        // deep clone the array to make sure list reloaded
        const cloned = _.cloneDeep(this.sentences);
        cloned[this.currentIndex].entities[this.highlightedIndex].namedEntity = selection;
        this.sentences = cloned;
      }
    },
    /**
     * Get sentences data fromm backend
     */
    async getSentences() {
      // directly disabled button after click and turn on the spinner
      this.loadingForData = true;
      this.editing = true;
      try {
        // USE THIS CODE PART FOR REAL DATA
        // const response = await axios.get(baseUrl + '/display_batch');
        // response.data.map(item => item.entities.map(entity => {

        // USE THIS CODE LINE FOR WORKING WITH DUMMY DATA
        display.map(item => item.entities.map(entity => {
        // --
          if (!entity.id) {
            entity.id = makeid(10);
          }
        }));

        this.sentences = display;
        this.loadingForData = false;
        this.hightlightIt(this.sentences[0].text);
      } catch (err) {
        this.loadingForData = false;
        this.editing = false;
        this.$swal({
          type: 'error',
          title: 'Oops',
          text: 'Your request is failed'
        });
        console.log(err);
      }      
    },
    /**
     * go to next sentence and highlight it
     */
    nextSentence() {
      if (this.currentIndex < this.sentences.length - 1) {
        this.currentIndex += 1;
        this.hightlightIt(this.sentences[this.currentIndex].text);
      }
    },
    /**
     * go to previous sentence and highlight it
     */
    previousSentence() {
      if (this.currentIndex > 0) {
        this.currentIndex -= 1;
        this.hightlightIt(this.sentences[this.currentIndex].text);
      }
    },
    /**
     * Save the result to backend
     */
    saveResultAction() {
      // USE THIS CODE PART FOR REAL DATA
      // axios.post(baseUrl + '/save_batch', this.sentences)
      //   .then(resp => {
      //     this.savedResult = resp.data.score;
      //     this.editing = false;
      //     this.sentences = '',
      //     this.currentSentence = '';
      //     this.currentIndex = 0;
      //     this.$swal({
      //         type: 'success',
      //         title: 'Save Result',
      //         text: 'The train result is' + this.savedResult
      //     });
      //   })
      //   .catch(err => {
      //     console.log(err);
      //     console.log(error.message)
      //     this.$swal({
      //       type: 'error',
      //       title: 'Oops',
      //       text: 'Your request is failed'
      //     });
      //   });

      // USE THIS CODE LINE FOR WORKING WITH DUMMY DATA
      this.savedResult = display;
      this.editing = false;
      this.sentences = '',
      this.currentSentence = '';
      this.currentIndex = 0;
    },
    /**
     * Getting the train result score
     */
    trainResultAction() {
      // USE THIS CODE PART FOR REAL DATA
      // axios.post(baseUrl + '/train_batch', this.sentences)
      //   .then(resp => {
      //     this.trainResult = resp.data.score
      //     this.$swal({
      //         type: 'success',
      //         title: 'Train Result',
      //         text: 'The train result is' + this.trainResult
      //     });
      //   })
      //   .catch(err => {
      //     this.$swal({
      //       type: 'error',
      //       title: 'Oops',
      //       text: 'Your request is failed'
      //     });
      //   });
    },
    /**
     * Add new NER List by adding empty boilerplates
     * unshift the data to make it placed on top
     */
    addNERList() {
      const id = Date.now();
      const empty = {
        namedEntity: '',
        index: [0, 0],
        entity_type: '',
        id: id
      }

      this.sentences[this.currentIndex].entities.unshift(empty);
      this.highlightedIndex = 0;
    },
    /**
     * Delete NER List by id
     * @param {string} idx - id of NER List need to be deleted
     */
    deleteNERList(idx) {
      this.sentences[this.currentIndex].entities.splice(idx, 1);
      this.hightlightIt(this.sentences[this.currentIndex].text);
    },
    /**
     * Edit certain NER List
     * @param {string} idx - id of NER List need to be edited
     */
    startEditingNERListProcess(idx) {
      this.highlightedIndex = idx;
    },
    /**
     * Finish the edit process of NER list and
     * @param {string} idx - id of NER List need to be deleted
     * @param {object} data - the data edited by user
     */
    finishEditingNERListProcess(idx, data) {
      this.highlightedIndex = null;
      this.sentences[this.currentIndex].entities[idx] = data;
      this.hightlightIt(this.sentences[this.currentIndex].text);
    },
    /**
     * Toggle the 'See Previous List' modal
     */
    async seePreviousList() {
      this.showPreviousList = !this.showPreviousList;
      try {
        // USE THIS CODE PART FOR REAL DATA
        // const response = await axios.get(baseUrl + '/see_previous_list');
        // this.previousListData = response.data;

        // USE THIS CODE LINE FOR WORKING WITH DUMMY DATA
        this.previousListData = display;
      } catch (err) {
        //this.loading = false;
        console.log(err);
      }      
    }
  },
};
</script>

<style></style>
